import styled, { css } from 'styled-components';
import { TABS_VARIANT_UNDERLINE, type TabsVariant } from './Tabs.constants';

interface RootProps {
  $bordered: boolean;
  $variant: TabsVariant;
}

/**
 * Tab strip — flex row with a hairline bottom border that visually
 * anchors the strip against the content below. `bordered={false}`
 * drops the divider for consumers that already own a separator (e.g.
 * sticky sub-headers that bake their own rule).
 */
export const Root = styled.div<RootProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing?.level3 ?? '8px'};
  padding: 6px ${({ theme: { spacing } }) => spacing?.level5 ?? '16px'};
  background: ${({ theme: { colors = {} } }) =>
    colors.surfaceContainer ?? '#181927'};
  ${({ $bordered, theme: { colors = {} } }) =>
    $bordered &&
    css`
      border-bottom: 1px solid
        ${colors.outlineVariant ?? '#25263A'};
    `}
  ${({ $variant }) =>
    $variant === TABS_VARIANT_UNDERLINE &&
    css`
      align-items: stretch;
      gap: 28px;
      padding: 0;
      background: transparent;
    `}
`;

interface TabProps {
  $active: boolean;
  $variant: TabsVariant;
}

/**
 * Single tab button. Pill-shaped active state matching the prototype's
 * `.proc-tabs__tab` rule. Inherits font so consumers can scope the
 * type system via a parent (e.g. a sub-header with a different font
 * stack) — only `font-size` + `font-weight` are pinned to keep the
 * "tab" affordance stable across embeds.
 */
export const Tab = styled.button<TabProps>`
  background: transparent;
  border: 0;
  padding: 6px 12px;
  border-radius: ${({ theme: { borderRadius = {} } }) =>
    borderRadius.full ?? '100px'};
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  font: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition:
    background-color
      ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'} ease,
    color ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'} ease,
    border-color ${({ theme: { motion } }) => motion?.duration?.fast ?? '120ms'} ease;

  & > svg {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
  }

  &:hover:not(:disabled) {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerHigh ?? '#1F2030'};
    color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  }

  &:focus-visible {
    outline: 2px solid
      ${({ theme: { colors = {} } }) => colors.primary ?? '#C1C1FF'};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  ${({ $active, theme: { colors = {} } }) =>
    $active &&
    css`
      background: ${colors.surfaceContainerHigh ?? '#1F2030'};
      color: ${colors.onSurface ?? '#E6E6F2'};
      font-weight: 600;
    `}

  ${({ $variant, $active, theme: { colors = {} } }) =>
    $variant === TABS_VARIANT_UNDERLINE &&
    css`
      flex: none;
      padding: 10px 4px 12px;
      white-space: nowrap;
      border-radius: 0;
      border-bottom: 2px solid ${$active ? (colors.primary ?? '#C1C1FF') : 'transparent'};
      font-size: 15px;
      background: transparent;

      &:hover:not(:disabled) {
        background: transparent;
      }
    `}
`;
